import { v4 } from "uuid";

import { VariableSet } from "../../../../Playground/types";
import { Variable } from "../../../types";

import DatasetPicker from "@/components/ui/DatasetPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";

const MAX_VARIABLE_SETS = 5;

interface DatasetImportProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVariablesFetched?: (variables: Record<string, string>, setName?: string) => void;
  onAddVariableSet?: (variableSet: VariableSet) => void;
  currentVariableSetCount?: number;
}

interface DatasetRowData {
  row: number;
  value: Record<string, string>;
}

interface SingleSelectResult {
  id: number;
  name: string;
  row: number;
  value: Record<string, string>;
}

interface MultiSelectResult {
  id: number;
  name: string;
  rows: DatasetRowData[];
}

type DatasetSelection = SingleSelectResult | MultiSelectResult | null;

const isMultiSelectResult = (selection: DatasetSelection): selection is MultiSelectResult =>
  selection !== null && "rows" in selection;

export const DatasetImport = ({
  isOpen,
  onOpenChange,
  onVariablesFetched,
  onAddVariableSet,
  currentVariableSetCount = 0,
}: DatasetImportProps) => {
  // When editing an existing set, the first row will replace it (not create a new one)
  // So we can select one more than the available slots
  const maxSelectable = MAX_VARIABLE_SETS - currentVariableSetCount + 1;

  // Handle selection and immediately import (called when Done is clicked in ExampleSelectionDialog)
  const handleSelectAndImport = (
    dataset:
      | { id: number; name: string; row: number; value: object }
      | { id: number; name: string; rows: { row: number; value: object }[] },
  ) => {
    const selection = dataset as DatasetSelection;
    if (!selection) {
      return;
    }

    const datasetName = selection.name;
    const rows = isMultiSelectResult(selection)
      ? selection.rows
      : [
          {
            row: (selection as SingleSelectResult).row,
            value: (selection as SingleSelectResult).value,
          },
        ];

    // First row replaces the current set being edited (example #1)
    if (rows.length > 0 && onVariablesFetched) {
      onVariablesFetched(rows[0].value, `${datasetName} (#1)`);
    }

    // Additional rows create new variable sets (example #2, #3, etc.)
    if (onAddVariableSet && rows.length > 1) {
      rows.slice(1).forEach(({ value }, index) => {
        const variables: Variable[] = Object.entries(value).map(([key, val]) => ({
          id: v4(),
          key,
          value: val,
        }));

        const newSet: VariableSet = {
          id: v4(),
          name: `${datasetName} (#${index + 2})`,
          variables,
        };

        onAddVariableSet(newSet);
      });
    }

    const rowCount = rows.length;
    if (rowCount === 1) {
      displayToast("Variables imported successfully", ToastType.success);
    } else {
      displayToast(
        `Variables imported into current set + ${rowCount - 1} new set${rowCount > 2 ? "s" : ""} created`,
        ToastType.success,
      );
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Import from Dataset</DialogTitle>
          <DialogDescription>
            Select a dataset to import rows from. You can select up to {maxSelectable} row
            {maxSelectable !== 1 ? "s" : ""}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="dataset-picker">Dataset</Label>
            <DatasetPicker
              onSelect={handleSelectAndImport}
              singleSelect={false}
              maxSelectable={maxSelectable}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
